import { useRef, useState, type PointerEvent, type ReactNode } from 'react'
import { useEditSession } from '@/core/store/editSession'
import {
  BOTTOM_SHEET_SNAP_TRANSITION,
  BottomSheetSnap,
  bottomSheetSnapHeightCss,
} from '@/core/store/uiState'
import { cn } from '@/lib/utils'
import { TEST_IDS } from '@/lib/testIds'

const SNAP_ORDER = [BottomSheetSnap.Peek, BottomSheetSnap.Half, BottomSheetSnap.Full] as const

export function BottomSheet({ children }: { children: ReactNode }) {
  const snap = useEditSession((state) => state.mobileSheetSnap)
  const setSnap = useEditSession((state) => state.setMobileSheetSnap)
  const startY = useRef<number | null>(null)
  const didDrag = useRef(false)
  const [dragOffset, setDragOffset] = useState(0)

  const cycleSnap = () => {
    const index = SNAP_ORDER.indexOf(snap)
    const next = SNAP_ORDER[(index + 1) % SNAP_ORDER.length]
    if (!next) throw new Error('Bottom sheet snap configuration is incomplete')
    setSnap(next)
  }

  const onPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId)
    startY.current = event.clientY
    didDrag.current = false
  }

  const onPointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    if (startY.current === null) return
    const nextOffset = event.clientY - startY.current
    if (Math.abs(nextOffset) > 4) didDrag.current = true
    setDragOffset(nextOffset)
  }

  const finishDrag = (event: PointerEvent<HTMLButtonElement>) => {
    if (startY.current === null) return
    const index = SNAP_ORDER.indexOf(snap)
    const releaseOffset = event.clientY - startY.current
    const direction = releaseOffset < -40 ? 1 : releaseOffset > 40 ? -1 : 0
    const nextIndex = Math.min(SNAP_ORDER.length - 1, Math.max(0, index + direction))
    const next = SNAP_ORDER[nextIndex]
    if (!next) throw new Error('Bottom sheet drag resolved to an invalid snap point')
    setSnap(next)
    startY.current = null
    setDragOffset(0)
  }

  return (
    <section
      data-testid={TEST_IDS.bottomSheet}
      data-snap={snap}
      className={cn(
        'absolute inset-x-0 bottom-0 z-40 overflow-hidden rounded-t-2xl border border-b-0 bg-background/98 shadow-[0_-12px_36px_rgb(0_0_0/0.24)] backdrop-blur',
        dragOffset === 0 ? 'transition-[height]' : 'transition-none',
      )}
      style={{
        height: bottomSheetSnapHeightCss(snap),
        transform: `translateY(${Math.max(-24, dragOffset)}px)`,
        transitionDuration: `${BOTTOM_SHEET_SNAP_TRANSITION.durationMs}ms`,
        transitionTimingFunction: BOTTOM_SHEET_SNAP_TRANSITION.timingFunction,
      }}
      aria-label="Editing tools"
    >
      <button
        data-testid={TEST_IDS.bottomSheetHandle}
        type="button"
        className="flex h-11 w-full touch-none items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        aria-label={`Resize controls panel, currently ${snap}`}
        onClick={() => {
          if (didDrag.current) {
            didDrag.current = false
            return
          }
          cycleSnap()
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
      >
        <span className={cn('h-1.5 w-12 rounded-full bg-muted-foreground/35', dragOffset !== 0 && 'bg-muted-foreground/60')} />
      </button>
      <div className="h-[calc(100%-2.75rem)] overflow-hidden">{children}</div>
    </section>
  )
}
